import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight, Download, Calendar } from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import strideLogo from '../assets/Final Logo Used in White Label.png';

export default function ThankYouPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDarkMode = useTheme((state) => state.theme);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    // Get session ID from URL params
    const sessionIdParam = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (paymentStatus !== 'success' || !sessionIdParam) {
      // Redirect to landing page if not a valid success redirect
      navigate('/', { replace: true });
      return;
    }
    
    setSessionId(sessionIdParam);
  }, [searchParams, navigate]);

  const handleGoToDashboard = () => {
    navigate('/');
  };

  if (!sessionId) {
    return null; // Will redirect to landing page
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} transition-colors duration-300`}>
      {/* Header */}
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src={strideLogo} 
              alt="Stride Logo" 
              className="h-10 w-auto"
            />
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Stride Sales Coach
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-8 md:p-12 text-center`}>
          {/* Success Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Thank You Message */}
          <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Thank You! 🎉
          </h1>
          
          <p className={`text-xl md:text-2xl mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Welcome to Stride Early Access Beta!
          </p>

          {/* Payment Success Details */}
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-6 mb-8`}>
            <div className="flex items-center justify-center space-x-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Payment Successful
              </h3>
            </div>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Your payment has been processed successfully. You're now part of our exclusive early access program!
            </p>
          </div>

          {/* What's Next Section */}
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-xl p-6 mb-8`}>
            <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              What's Next?
            </h3>
            <div className="space-y-4 text-left">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Check Your Email
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    We've sent you a confirmation email with your receipt and next steps.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Setup Call Scheduled
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Our team will reach out within 48 hours to schedule your onboarding call.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Download className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Early Access Portal
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Access to the beta platform will be provided during your onboarding call.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          {/* <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6 mb-8`}>
            <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Need Help?
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-3`}>
              If you have any questions or need assistance, please don't hesitate to contact us.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
              <a 
                href="mailto:support@stridesalescoach.com" 
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>support@stridesalescoach.com</span>
              </a>
              <a 
                href="https://calendly.com/stridesalescoach" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Book a Call</span>
              </a>
            </div>
          </div> */}

          {/* CTA Button */}
          <button
            onClick={handleGoToDashboard}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Continue to Homepage
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>

          {/* Session ID for Reference */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Reference ID: {sessionId}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}